import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@screentone/addon-auth-wrapper';
import {
  Box,
  Button,
  FormHelperText,
  Group,
  IconCopy,
  Input,
  Loader,
  Textarea,
  Typography,
  Wrapper
} from '@screentone/core';

import { ResponsiveLoader } from 'components';
import { useDataModelContext } from 'contexts/datamodel/useDataModel';
import { CollectionDtoMetadata, PagesDtoForCollectionIdQuery } from 'data/generated/graphql';
import { usePublicationSettings } from 'hooks/publication-settings';
import { useIsAdmin, useIsSuper } from 'hooks/roles';
import { AuraEmployeeResponse, useAuraQuery } from 'hooks/useAuraQuery';
import env from 'utils/env';
import { reportError } from 'utils/errorBoundary';
import { isValidURL } from 'utils/url';
import styles from './CollectionInfoBoxesSection.module.scss';
import collectionEditStyles from '../../CollectionEdit.module.scss';
import { CollectionExternalId } from '../collection-external-id/CollectionExternalId';
import { TOffPlatformInput } from '../collection-off-platform-overlay/CollectionOffPlatformOverlay';
import CollectionParameters from '../collection-parameters/collection-parameters';
import { CollectionPublishingButtons } from '../collection-publishing-buttons/CollectionPublishingButtons';

interface CollectionInfoBoxesSectionProps {
  pagesForCollection?: PagesDtoForCollectionIdQuery;
  pagesAreLoading: boolean;
  hasCollectionChanged: boolean;
  isUpdating: boolean;
  handleCopy: () => Promise<void>;
  isCopying: boolean;
  handleArchive: () => Promise<void>;
  isArchiving: boolean;
  handleOffPlatformConvert: (input: TOffPlatformInput) => Promise<void>;
  isConvertingToOffPlatform: boolean;
  waitingForAllesseh: boolean;
  isReadOnly?: boolean;
  onValidationChange?: (hasError: boolean) => void;
}

export const CollectionInfoBoxesSection = ({
  pagesForCollection,
  pagesAreLoading,
  hasCollectionChanged,
  isUpdating,
  handleCopy,
  isCopying,
  handleArchive,
  isArchiving,
  handleOffPlatformConvert,
  isConvertingToOffPlatform,
  waitingForAllesseh,
  isReadOnly = false,
  onValidationChange
}: CollectionInfoBoxesSectionProps) => {
  const isAdmin = useIsAdmin();
  const isSuper = useIsSuper();
  const navigate = useNavigate();
  const { currentProperty } = useAuth();
  const { metadata, setNewMetadata } = useDataModelContext();
  const { data: publicationSettings } = usePublicationSettings();
  const { data: creatorUserData, isLoading: isFetchingCreatorUserData } = useAuraQuery(metadata.creatorUser.djUserId, {
    currentProperty
  });
  const creatorUserDataTyped = creatorUserData as AuraEmployeeResponse | undefined;
  const creatorUserText = creatorUserDataTyped
    ? `${creatorUserDataTyped.first_name ?? ''} ${creatorUserDataTyped.last_name ?? ''}`
    : metadata.creatorUser.djUserId;
  const { data: revisorUserData, isLoading: isFetchingRevisorUserData } = useAuraQuery(
    metadata.revisorUser?.djUserId ?? '',
    { currentProperty }
  );
  const revisorUserDataTyped = revisorUserData as AuraEmployeeResponse | undefined;
  const revisorUserText = revisorUserDataTyped
    ? `${revisorUserDataTyped.first_name ?? ''} ${revisorUserDataTyped.last_name ?? ''}`
    : metadata.revisorUser?.djUserId;

  const isAllessehIdEditable = (isAdmin || isSuper) && !metadata.allessehId;

  const manuallyCreatedAllessehIdStart = `${env.UCS_COLLECTION_PREFIX}_${currentProperty}_COLLECTION_`.toUpperCase();
  const [manuallyCreatedAllessehId, setManuallyCreatedAllessehId] = useState(() => {
    const initial = (metadata as CollectionDtoMetadata).manuallyCreatedAllessehId ?? '';

    if ((!isAdmin && !isSuper) || metadata.allessehId) {
      return undefined;
    }

    return (
      initial.startsWith(manuallyCreatedAllessehIdStart) ? initial : manuallyCreatedAllessehIdStart + initial
    ).toUpperCase();
  });

  const handleNameChange: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    setNewMetadata({ name: e.target.value });
  };

  const handleNotesChange: React.ChangeEventHandler<HTMLTextAreaElement> = (e) => {
    setNewMetadata({ notes: e.target.value });
  };

  const collectionMetadata = metadata as CollectionDtoMetadata;
  const [headline, setHeadline] = useState(collectionMetadata.headline ?? '');
  const [subheadline, setSubheadline] = useState(collectionMetadata.subheadline ?? '');
  const initialImageUrl = collectionMetadata.imageUrl ?? '';
  const [imageUrl, setImageUrl] = useState(initialImageUrl);
  const [imageUrlError, setImageUrlError] = useState(() =>
    initialImageUrl !== '' && !isValidURL(initialImageUrl) ? 'Invalid URL' : ''
  );

  useEffect(() => {
    if (initialImageUrl !== '' && !isValidURL(initialImageUrl)) {
      onValidationChange?.(true);
    }
    // Only run on mount to propagate initial validation state to parent
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleHeadlineChange: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    setHeadline(e.target.value);
    setNewMetadata({ headline: e.target.value } as Partial<CollectionDtoMetadata>);
  };

  const handleSubheadlineChange: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    setSubheadline(e.target.value);
    setNewMetadata({ subheadline: e.target.value } as Partial<CollectionDtoMetadata>);
  };

  const handleImageUrlChange: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    const { value } = e.target;
    setImageUrl(value);
    if (value !== '' && !isValidURL(value)) {
      setImageUrlError('Invalid URL');
      onValidationChange?.(true);
    } else {
      setImageUrlError('');
      onValidationChange?.(false);
      setNewMetadata({ imageUrl: value || undefined } as Partial<CollectionDtoMetadata>);
    }
  };

  const handleManuallyCreatedAllessehIdChange: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    const { value: fullInput } = e.target;
    const expectedPrefix = manuallyCreatedAllessehIdStart;
    const oldValue = manuallyCreatedAllessehId ?? '';
    const prevLength = oldValue.length;
    const nextLength = fullInput.length;

    const typedOneChar = nextLength === prevLength + 1;

    let suffix: string;

    if (typedOneChar && !fullInput.startsWith(expectedPrefix)) {
      // User likely typed into the prefix – fix it
      suffix = fullInput.slice(expectedPrefix.length + 1);
    } else {
      // Normal suffix typing
      suffix = fullInput.slice(expectedPrefix.length);
    }

    const cleanedSuffix = suffix.replace(/[^a-zA-Z0-9_-]/g, '');
    const value = (expectedPrefix + cleanedSuffix).toUpperCase();

    setManuallyCreatedAllessehId(value);
    if (manuallyCreatedAllessehIdStart !== value) {
      setNewMetadata({ manuallyCreatedAllessehId: value });
    } else {
      setNewMetadata({ manuallyCreatedAllessehId: undefined });
    }
  };

  return (
    <div className={collectionEditStyles.infoScrollable} data-testid="collection-info-box-section-container">
      <Box data-testid="collection-info-box-section-conf">
        <Box.Title data-testid="collection-info-box-section-conf-title">Configuration</Box.Title>
        <Box.Content data-testid="collection-info-box-section-conf-content">
          <Wrapper data-testid="collection-info-box-section-conf-name" margin={{ bottom: 'md' }}>
            <Typography
              data-testid="collection-info-box-section-conf-name-label"
              size="sm"
              weight="bold"
              margin={{ bottom: 'sm' }}
            >
              Name
            </Typography>
            <Input
              data-testid="collection-info-box-section-conf-name-input"
              type="text"
              placeholder="Name of collection"
              value={metadata.name}
              onChange={handleNameChange}
              disabled={isReadOnly}
            />
          </Wrapper>
          <Wrapper data-testid="collection-info-box-section-conf-notes" margin={{ bottom: 'md' }}>
            <Typography
              data-testid="collection-info-box-section-conf-notes-label"
              size="sm"
              weight="bold"
              margin={{ bottom: 'sm' }}
            >
              Notes
            </Typography>
            <Textarea
              data-testid="collection-info-box-section-conf-notal-textbox"
              value={('notes' in metadata && metadata.notes) || ''}
              onChange={handleNotesChange}
              disabled={isReadOnly}
            />
          </Wrapper>
          <Wrapper data-testid="collection-info-box-section-conf-id">
            <Typography
              data-testid="collection-info-box-section-conf-id-label"
              size="sm"
              weight="bold"
              margin={{ bottom: 'sm' }}
            >
              Collection ID
            </Typography>
            {isAllessehIdEditable ? (
              <>
                <FormHelperText>
                  The ID will start with {manuallyCreatedAllessehIdStart}. If left blank, one will be auto-generated.
                  Use only letters, numbers, dashes, or underscores.
                </FormHelperText>
                <Input
                  data-testid="collection-info-box-section-conf-id-input"
                  type="text"
                  placeholder="ID of collection"
                  onChange={handleManuallyCreatedAllessehIdChange}
                  disabled={isReadOnly || !isAllessehIdEditable}
                  value={manuallyCreatedAllessehId ?? (metadata.allessehId || '')}
                  readOnly={isReadOnly || !isAllessehIdEditable}
                />
              </>
            ) : (
              <Group gap="xs">
                <Typography data-testid="collection-info-box-conf-id-text">{`${metadata.allessehId.slice(
                  0,
                  19
                )}...`}</Typography>
                <Button
                  data-testid="collection-info-box-conf-id-copy-button"
                  icon={IconCopy}
                  tertiary
                  onClick={() => {
                    navigator.clipboard.writeText(metadata.allessehId).catch(() => {
                      reportError('Failed to copy Collection ID to clipboard');
                    });
                  }}
                />
              </Group>
            )}
          </Wrapper>
          <CollectionExternalId />
        </Box.Content>
      </Box>
      <CollectionParameters isReadOnly={isReadOnly} />
      <Box data-testid="collection-info-box-section-published-content" margin={{ top: 'md' }}>
        <Box.Title data-testid="collection-info-box-section-published-content-title">Published Content</Box.Title>
        <Box.Content data-testid="collection-info-box-section-published-content-body">
          <Wrapper data-testid="collection-info-box-section-conf-headline" margin={{ bottom: 'md' }}>
            <Typography
              data-testid="collection-info-box-section-conf-headline-label"
              size="sm"
              weight="bold"
              margin={{ bottom: 'sm' }}
            >
              Headline
            </Typography>
            <Input
              data-testid="collection-info-box-headline-input"
              type="text"
              placeholder="Add headline"
              value={headline}
              onChange={handleHeadlineChange}
              disabled={isReadOnly}
            />
          </Wrapper>
          <Wrapper data-testid="collection-info-box-section-conf-subheadline" margin={{ bottom: 'md' }}>
            <Typography
              data-testid="collection-info-box-section-conf-subheadline-label"
              size="sm"
              weight="bold"
              margin={{ bottom: 'sm' }}
            >
              Subhed
            </Typography>
            <Input
              data-testid="collection-info-box-subheadline-input"
              type="text"
              placeholder="Add subhed"
              value={subheadline}
              onChange={handleSubheadlineChange}
              disabled={isReadOnly}
            />
          </Wrapper>
          <Wrapper data-testid="collection-info-box-section-conf-image-url">
            <Typography
              data-testid="collection-info-box-section-conf-image-url-label"
              size="sm"
              weight="bold"
              margin={{ bottom: 'sm' }}
            >
              Image URL
            </Typography>
            <Input
              data-testid="collection-info-box-image-url-input"
              type="text"
              placeholder="Add Image URL"
              value={imageUrl}
              onChange={handleImageUrlChange}
              disabled={isReadOnly}
            />
            {imageUrlError && (
              <FormHelperText error data-testid="collection-info-box-image-url-error">
                {imageUrlError}
              </FormHelperText>
            )}
          </Wrapper>
        </Box.Content>
      </Box>
      {!!metadata.allessehId && (
        <>
          <Box data-testid="collection-info-box-section-edit" margin={{ top: 'md' }}>
            <Box.Title data-testid="collection-info-box-section-edit-title">Editors</Box.Title>
            <Box.Content data-testid="collection-info-box-section-edit-content">
              <Wrapper data-testid="collection-info-box-section-edit-update" className={styles.editorItem}>
                <Typography data-testid="collection-info-box-section-edit-update-user">
                  {isFetchingRevisorUserData ? <Loader size="md" /> : revisorUserText}
                </Typography>
                <Typography
                  data-testid="collection-info-box-section-edit-update-date"
                  variant="note"
                  margin={{ left: 'sm' }}
                >
                  Last Updated By
                </Typography>
              </Wrapper>
              <Wrapper
                data-testid="collection-info-box-section-edit-creator"
                className={styles.editorItem}
                margin={{ top: 'sm' }}
              >
                <Typography data-testid="collection-info-box-section-edit-creator-user">
                  {isFetchingCreatorUserData ? <Loader size="md" /> : creatorUserText}
                </Typography>
                <Typography
                  data-testid="collection-info-box-section-edit-creator-label"
                  variant="note"
                  margin={{ left: 'sm' }}
                >
                  Creator
                </Typography>
              </Wrapper>
            </Box.Content>
          </Box>
          <Box data-testid="collection-info-box-section-pages" margin={{ top: 'md' }}>
            <Box.Title data-testid="collection-info-box-section-pages-title">Pages</Box.Title>
            <Box.Content data-testid="collection-info-box-section-pages-content">
              {pagesAreLoading && <ResponsiveLoader />}
              {!pagesAreLoading && (
                <>
                  {pagesForCollection?.pagesDTOForCollectionId.length === 0 ? (
                    <Typography data-testid="collection-info-box-section-pages-none-label" variant="note">
                      No pages linked
                    </Typography>
                  ) : (
                    pagesForCollection?.pagesDTOForCollectionId.map((pageForCollection, index) => {
                      const {
                        metadata: { allessehId, publishUtc }
                      } = pageForCollection.latestPage;
                      const slugMappings = publicationSettings?.publicationSetting.slugMappings;
                      const [slug] =
                        Object.entries(slugMappings ?? {}).find(([, groupKey]) => groupKey === allessehId) ?? [];
                      const pageId = slug ?? allessehId;
                      const linkUrl = `/${currentProperty ?? ''}/pages/${pageId}/version/${publishUtc!}`;
                      return (
                        <Button
                          data-testid="collection-info-box-section-pages-page-button"
                          className={styles.pageButton}
                          margin={{ bottom: 'xs' }}
                          key={index}
                          tertiary
                          fullWidth
                          onClick={() => navigate(linkUrl)}
                        >
                          {pageForCollection.latestPage.metadata.name} ({pageForCollection.numRevisions})
                        </Button>
                      );
                    })
                  )}
                </>
              )}
            </Box.Content>
          </Box>
          <CollectionPublishingButtons
            hasCollectionChanged={hasCollectionChanged}
            pagesForCollection={pagesForCollection}
            isUpdating={isUpdating}
            handleCopy={handleCopy}
            isCopying={isCopying}
            handleArchive={handleArchive}
            isArchiving={isArchiving}
            handleOffPlatformConvert={handleOffPlatformConvert}
            isConvertingToOffPlatform={isConvertingToOffPlatform}
            waitingForAllesseh={waitingForAllesseh}
          />
        </>
      )}
    </div>
  );
};
